import type { SearchResponse } from "@/features/search/api/queries";
import { isTermsFacet } from "@/features/search/api/types";

/**
 * How many photos a result card can show: the first, and the second it
 * crossfades to on hover (`ProductCardImage`).
 */
export const HYDRATED_IMAGES_PER_PRODUCT = 2;

/**
 * The search response as the browser needs it on first paint.
 *
 * The whole response is serialised into the page HTML as react-query's
 * `dehydratedState`, and on a live category page that was 119 KB, a quarter of
 * the document. Two fields the grid never renders made up a third of it:
 * `description` (19 KB of prose per 24 results) and every photo of every
 * product (23 KB; cards show two). The rest of a product is what
 * `ProductCardProduct` reads, plus a few short ids, and stays.
 *
 * The filter panel then became the biggest thing left: 63.5 KB of facets on a
 * live NL category page, of which 14 KB is a `id` UUID per term value that no
 * client code reads (WHO-5547). The panel keys its rows off `value` and toggles
 * with `facet.key` + `value.value` — the slug the URL and the service both use.
 * `meta` and `pageInfo` are rendered as they arrive (headings, pagination) and
 * are left alone.
 *
 * Nulling rather than deleting keeps the object a valid `Product`/`Facet`: the
 * client types the hydrated data the same way it types a fresh response, so the
 * shape must not change, only the payload. A term id is already `string | null`
 * in the schema (enum facets have no DB entity), so every consumer must handle
 * null today.
 *
 * ⚠️ If the client ever needs a term's id — sending it back to the service,
 * keying a cache on it — this transform is what took it away. Drop the id line
 * rather than reconstructing it on the client.
 *
 * Returns a new object; the server keeps the full response for its own use
 * (404 decisions, SEO tags), only the hydrated copy is slimmed.
 */
export const slimSearchResponseForHydration = <T extends SearchResponse>(
  response: T
): T => {
  return {
    ...response,
    data: response.data.map(product => ({
      ...product,
      ...(product.promotion !== undefined ? { promotion: null } : {}),
      description: null,
      images: product.images.slice(0, HYDRATED_IMAGES_PER_PRODUCT),
    })),
    facets: response.facets.map(facet =>
      isTermsFacet(facet)
        ? {
            ...facet,
            values: facet.values.map(value => ({ ...value, id: null })),
          }
        : facet
    ),
  };
};

/*
 * WHO-5547, round 2: defaults are left out of the HTML and put back before the
 * cache sees them.
 *
 * After the slimming above, most of what is left in the hydrated response is
 * the filter panel, and most of a facet value is the same three fields at their
 * default: `"id":null,"selected":false,"locked":false` on ~500 values is ~20 KB
 * on a live NL category page. Products repeat a dozen `null`s each on top.
 *
 * The server drops a field when it holds its default (`packDehydratedSearchState`,
 * in `SearchPage.server`), and `_app` puts it back (`unpackDehydratedSearchState`)
 * before `HydrationBoundary` hands the state to react-query, on the server render
 * and in the browser alike. The cache therefore holds exactly the object it held
 * before: every consumer, the favourite toggle's cache writes and the refetch on
 * a filter change see the same data. `unpack(pack(x))` deep-equals `x`, which the
 * spec asserts; unpacking data that was never packed is a no-op.
 *
 * ⚠️ A new field that is often at a default belongs in the lists below, never a
 * field whose absence would mean something different from its default.
 */

/** Facet-value fields that are dropped from the HTML when they hold this value. */
const TERM_VALUE_DEFAULTS = { id: null, selected: false, locked: false };
const CHOICE_VALUE_DEFAULTS = { selected: false, locked: false };

/** Product fields that are dropped from the HTML when `null`. All nullable in the schema. */
export const PACKED_NULL_PRODUCT_FIELDS = [
  "description",
  "condition",
  "quality",
  "discountPercentage",
  "bidFrom",
  "soldAtPrice",
  "width",
  "height",
  "depth",
  "gender",
  "shape",
  "isArt",
  "premium",
  "brandId",
  "favoriteId",
] as const;

const PRODUCT_NULL_DEFAULTS: Record<string, null> = Object.fromEntries(
  PACKED_NULL_PRODUCT_FIELDS.map(field => [field, null])
);

type Loose = Record<string, unknown>;

const isObject = (value: unknown): value is Loose =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const withoutDefaults = (value: Loose, defaults: Loose): Loose => {
  const packed: Loose = {};

  for (const [key, field] of Object.entries(value)) {
    if (!(key in defaults) || defaults[key] !== field) {
      packed[key] = field;
    }
  }

  return packed;
};

/** Defaults first, then the packed fields over them; key order is the defaults' first. */
const withDefaults = (value: Loose, defaults: Loose): Loose => {
  for (const key of Object.keys(defaults)) {
    if (!(key in value)) {
      return { ...defaults, ...value };
    }
  }

  return value;
};

const mapFacetValues = (
  facets: unknown,
  map: (value: Loose, defaults: Loose) => Loose
): unknown =>
  Array.isArray(facets)
    ? facets.map(facet => {
        if (!isObject(facet) || !Array.isArray(facet.values)) {
          return facet;
        }

        const defaults =
          facet.type === "terms"
            ? TERM_VALUE_DEFAULTS
            : facet.type === "choice"
            ? CHOICE_VALUE_DEFAULTS
            : null;

        return defaults
          ? {
              ...facet,
              values: facet.values.map(value =>
                isObject(value) ? map(value, defaults) : value
              ),
            }
          : facet;
      })
    : facets;

const mapResponse = (
  data: unknown,
  map: (value: Loose, defaults: Loose) => Loose
): unknown => {
  if (!isObject(data) || !Array.isArray(data.data)) {
    return data;
  }

  return {
    ...data,
    data: data.data.map(product =>
      isObject(product) ? map(product, PRODUCT_NULL_DEFAULTS) : product
    ),
    facets: mapFacetValues(data.facets, map),
  };
};

const mapSearchQueries = (
  state: unknown,
  map: (value: Loose, defaults: Loose) => Loose
): unknown => {
  if (!isObject(state) || !Array.isArray(state.queries)) {
    return state;
  }

  return {
    ...state,
    queries: state.queries.map(query => {
      if (
        !isObject(query) ||
        !Array.isArray(query.queryKey) ||
        query.queryKey[0] !== "search" ||
        !isObject(query.state)
      ) {
        return query;
      }

      return {
        ...query,
        state: { ...query.state, data: mapResponse(query.state.data, map) },
      };
    }),
  };
};

/** Server side: the dehydrated search state with default-valued fields left out. */
export const packDehydratedSearchState = (state: unknown): unknown =>
  mapSearchQueries(state, withoutDefaults);

/** `_app`: the packed state with every left-out field back at its default. */
export const unpackDehydratedSearchState = (state: unknown): unknown =>
  mapSearchQueries(state, withDefaults);
